import { Fragment, useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import AccountTab from '../../components/account/AccountTab';
import AccountTabModal from '../../components/account/AccountTabModal';
import AccountProfileSection from '../../components/account/AccountProfileSection';
import AccountSidebar from '../../components/account/AccountSidebar';
import VideoCard from '../../components/common/VideoCard';
import useSelectVideo from '../../hooks/useSelectVideo';
import SkeletonVideoCard from '../../components/skeleton/SkeletonVideoCard';
import { getReshare, resetLoadingMore } from '../../redux/ApiSlice/ResharedSlice';

export default function Repost() {
    const dispatch = useDispatch();
    const location = useLocation();
    const repostData = useSelector((state) => state.resharedPosts);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [show, setShow] = useState(false);
    const [activeTab, setActiveTab] = useState("");
    const ref = useRef(null);
    const repostScrollRef = useRef(null);
    const selectVideo = useSelectVideo();

    const {
        reSharedList = [],
        loading = false,
        loadingMore = false,
        nextPage = null,
        totalCount = 0,
    } = repostData || {};

    const handleClickOutside = () => {
        setShow(false);
        setActiveTab("");
    };
    useOutsideClick(ref, handleClickOutside);

    const openDrawer = () => setIsDrawerOpen(true);
    const closeDrawer = () => setIsDrawerOpen(false);

    const handleSelectVideo = (id) => {
        const videoList = reSharedList?.map((item) => item?.original_post);
        selectVideo(id, videoList, location.pathname, 'repost', nextPage);
    };

    const fetchData = useCallback(async () => {
        try {
            await dispatch(getReshare());
        } catch (error) {
          console.error(error);
        }
    }, [dispatch]);

    const handleRepostScroll = useCallback(() => {
        const container = repostScrollRef.current;
        if (!container || !nextPage || loadingMore) {
            return;
        }

        const hasReachedBottom =
            container.scrollTop + container.clientHeight >= container.scrollHeight - 10;

        if (hasReachedBottom) {
            dispatch(
                getReshare({
                    nextUrl: nextPage,
                    isLoadMore: true,
                })
            );
        }
    }, [dispatch, loadingMore, nextPage]);

    useEffect(() => {

        window.scrollTo({ top: 0, behavior: 'smooth' });

        dispatch(resetLoadingMore());
        fetchData();
    }, [fetchData, dispatch]);

    useEffect(() => {
        const container = repostScrollRef.current;
        if (!container) return;

        container.addEventListener("scroll", handleRepostScroll);

        return () => {
            container.removeEventListener("scroll", handleRepostScroll);
        };
    }, [handleRepostScroll]);

    return (
        <Fragment>
            <div
                ref={repostScrollRef}
                className={`bg-[#fff] dark:bg-[#1E1D21] overflow-hidden h-full relative ${isDrawerOpen ? "" : "overflow-y-scroll"}`}
            >
                <AccountProfileSection
                    handleOpenSideBar={openDrawer}
                    handleCloseSideBar={closeDrawer}
                    isDrawerOpen={isDrawerOpen}
                    setActiveTab={setActiveTab}
                    setShow={setShow}
                    show={show}
                />

                <div className="sticky top-0 z-30">
                    <AccountTab activeTab={"Reposts"} />
                </div>

                <div className="w-full h-fit">
                    {!loading && totalCount > 0 && (
                        <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 px-3 pt-2">
                            {totalCount} {totalCount === 1 ? "Video" : "Videos"}
                        </p>
                    )}
                    {loading ? (
                        <SkeletonVideoCard />
                    ) : reSharedList && reSharedList?.length !== 0 ? (
                        <div className="grid grid-cols-3 p-2 gap-1">
                            {reSharedList?.map((item) => (
                                <VideoCard
                                    key={item?.id || item?.original_post?.id}
                                    belowDecs={true}
                                    threeDots={false}
                                    id={item?.original_post?.id}
                                    selectVideo={handleSelectVideo}
                                    views={item?.original_post?.views}
                                    video={item?.original_post?.video_url}
                                    body={item?.original_post?.post_description}
                                    user_id={item?.original_post?.created_by?.id}
                                    authorId={item?.original_post?.created_by?.id}
                                    view_count={item?.original_post?.view_count}
                                    likes_count={item?.original_post?.likes_count}
                                    avatar_url={item?.original_post?.created_by?.avatar_url}
                                    thumbnail={item?.original_post?.thumbnail_url}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="flex justify-center items-center">
                            <p className="text-[12px] mt-20 text-gray-600 dark:text-gray-500">
                                Your reposted videos will appear here.
                            </p>
                        </div>
                    )}
                    {loadingMore && (
                        <div className="flex justify-center items-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-12 overflow-hidden">
                    <div className="col-span-12">
                        <AccountSidebar
                            isDrawerOpen={isDrawerOpen}
                            setIsDrawerOpen={setIsDrawerOpen}
                            openDrawer={openDrawer}
                            closeDrawer={closeDrawer}
                        />
                    </div>
                </div>
            </div>

            {show && (
                <div className={"absolute h-full w-full top-0 overflow-hidden"}>
                    <div
                        className="absolute h-screen w-full top-0 opacity-100 bg-opacity-70 bg-black z-50"
                        onClick={handleClickOutside}
                    />
                    <div
                        ref={ref}
                        className="absolute top-0 left-4 z-50 w-[90%] h-[-webkit-fill-available] mt-4 mb-3 flex justify-center rounded-lg  bg-black duration-1000 transform opacity-100"
                    >
                        <AccountTabModal
                            ref={ref}
                            // data={data}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                    </div>
                </div>
            )}
        </Fragment>
    )
}
